
import { Component, OnChanges, OnDestroy, OnInit, Input, SimpleChanges, inject, WritableSignal, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, NavigationEnd } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DropdownMaterial, TopMenuData } from '@k12/models';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

export interface SimpleMenuItem {
  label: string;
  command?: () => void;
}

export const WORK_QUEUE_ROUTE = 'work-queue';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-subheader',
  imports: [MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  templateUrl: './subheader.component.html',
  styleUrl: './subheader.component.scss'
})

export class LibSubheaderComponent implements OnChanges, OnInit, OnDestroy {
  @Input() isAdmin: boolean = false;
  @Input() topMenu: TopMenuData[] | undefined;

  activeItem: WritableSignal<string | null> = signal(null);
  currentUrl: WritableSignal<string> = signal('');
  isLoggedin: boolean = false;
  userMenu: SimpleMenuItem[] | undefined;

  private readonly router = inject(Router);
  private routeSubscription?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['topMenu']) {
      this.updateActiveItem();
    }
  }

  ngOnInit(): void {
    this.userMenu = [
      {
        label: 'Logout',
        command: () => {
          this.isLoggedin = !this.isLoggedin;
        }
      }
    ];

    // Subscribe to route changes
    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set((event as NavigationEnd).urlAfterRedirects);
        this.updateActiveItem();
      });
    this.updateActiveItem();
  }

  private matchDropdownRoute(dropdownItems: DropdownMaterial[], path: string): string | null {
    //longest prefix match
    let prefixMatch: string | null = null;
    let prefixMatchLength = -1;

    for (const item of dropdownItems) {
      const dropdownRoute = item.route;
      if (path === dropdownRoute) {
        return dropdownRoute;
      }
      if (path.startsWith(dropdownRoute + '/') && dropdownRoute.length > prefixMatchLength) {
        prefixMatchLength = dropdownRoute.length;
        prefixMatch = dropdownRoute;
      }
    }
    return prefixMatch;
  }

  private updateActiveItem(): void {
    if (!this.topMenu?.length || !this.currentUrl()) {
      this.activeItem.set('');
      return;
    }

    const path = this.currentUrl().split('?')[0].replace(/^\//, '');
    let currentRoute = path;
    let activated = false;

    if (currentRoute === '' || currentRoute === 'Home') {
      currentRoute = 'home';
      this.activeItem.set('home');
      activated = true;
    }

    if (
      currentRoute.includes(WORK_QUEUE_ROUTE) &&
      (currentRoute.includes('create') || currentRoute.includes('tasks'))
    ) {
      this.activeItem.set('tasks');
      activated = true;
    } else {
      if ((this.topMenu?.length ?? 0) > 0) {
        const result = this.setRoute(currentRoute);
        this.activeItem.set(result.activeItem);
        activated = result.activated;
      }

      if (!activated && currentRoute.includes(WORK_QUEUE_ROUTE)) {
        this.activeItem.set(WORK_QUEUE_ROUTE);
        activated = true;
      }
    }

    if (!activated) {
      this.activeItem.set('');
    }
  }

  private setRoute(currentRoute: string): { activeItem: string | null; activated: boolean } {
    for (const menu of this.topMenu ?? []) {
      if (menu.route === currentRoute) {
        return { activeItem: menu.route ?? '', activated: true };
      }

      if (menu.dropdown?.length) {
        const matched = this.matchDropdownRoute(menu.dropdown, currentRoute);
        if (matched !== null) {
          return { activeItem: matched, activated: true };
        }
      }
    }
    return { activeItem: null, activated: false };
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  navigatePage(route: string | undefined): void {
    if (route === 'home') {
      this.router.navigate(['/']);
      this.activeItem.set('home');
    } else if (route) {
      this.router.navigate(['/' + route]);
      this.activeItem.set(route);
    }
  }

  setActiveItem(item: string): void {
    this.activeItem.set(item);
  }

  async executeCommand(
    command: (() => Promise<{ status: boolean; route: string }>) | undefined,
    route: string
  ): Promise<void> {
    if (command) {
      try {
        const result = command();
        if (result instanceof Promise) {
          const completed = await result;
          const parsedCompleted = completed.route.split('/')[0];
          if (parsedCompleted && parsedCompleted !== '') {
            this.setActiveItem(parsedCompleted);
          }
        }
      } catch (error) {
        console.error('Command execution failed:', error);
        this.setActiveItem(route);
      }
    }
  }

  isMenuItemActive(menuButton: TopMenuData): boolean {
    const activeItem = this.activeItem();
    if (!activeItem) {
      return false;
    }
    if (activeItem === menuButton.route) {
      return true;
    }
    return menuButton.dropdown?.some(item => activeItem === item.route) ?? false;
  }
}
